/** Words and numbers for the Scans dashboard (DEC-191). No em-dashes (DEC-069). */
import type { LiveScanSummary, PendingScan, ScanStatusResponse, Severity } from '../../types/api'
import { isUploadScan, repoLabel } from '../../utils/format'
import type { ScanStatusKey } from './scanStatus'

export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']
export const SEVERITY_WORD: Record<Severity, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low',
}
/** Marks, not text: gated at 3:1 as graphical objects (DEC-131). */
export const SEVERITY_MARK: Record<Severity, string> = {
  critical: 'bg-mark-critical', high: 'bg-mark-high', medium: 'bg-mark-medium', low: 'bg-mark-low',
}

export type SeverityCounts = Partial<Record<Severity, number>>

export function sevTotal(sev: SeverityCounts | undefined): number {
  if (!sev) return 0
  return SEVERITIES.reduce((n, k) => n + (sev[k] ?? 0), 0)
}

/** "1 Critical, 111 High" (the two that drive a decision), or every severity. */
export function sevLine(sev: SeverityCounts | undefined, every = false): string {
  if (!sev) return ''
  const keys = every ? SEVERITIES : (['critical', 'high'] as Severity[])
  return keys.filter((k) => sev[k]).map((k) => `${sev[k]} ${SEVERITY_WORD[k]}`).join(', ')
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function startOfDay(t: number): number {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function pad(n: number): string {
  return (n < 10 ? '0' : '') + n
}

export function hm(iso: string): string {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** "Today", "Yesterday" or "Oct 3". */
export function dayLabel(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime()
  const today = startOfDay(now)
  if (t >= today) return 'Today'
  if (t >= today - 86_400_000) return 'Yesterday'
  const d = new Date(iso)
  return `${MON[d.getMonth()]} ${d.getDate()}`
}

/** "Today · 14:20", "Yesterday · 09:05", "Oct 3, 2026 · 14:20". The year stays (DEC-184). */
export function whenLabel(iso: string, now = Date.now()): string {
  const day = dayLabel(iso, now)
  if (day === 'Today' || day === 'Yesterday') return `${day} · ${hm(iso)}`
  const d = new Date(iso)
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${hm(iso)}`
}

/** "Oct 12, 2026": a day, with its year (DEC-184). */
export function dateLabel(iso: string): string {
  const d = new Date(iso)
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

export function agoLabel(iso: string, now = Date.now()): string {
  const m = Math.floor((now - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function durLabel(fromIso: string, toIso: string | number): string {
  const to = typeof toIso === 'number' ? toIso : new Date(toIso).getTime()
  const s = Math.max(1, Math.round((to - new Date(fromIso).getTime()) / 1000))
  if (s < 60) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min`
  return `${Math.floor(m / 60)} h ${m % 60} min`
}

/** The date groups of the By date tab, in order. */
export const DATE_BUCKETS = ['Today', 'Yesterday', 'Earlier this week', 'Last week', 'Older'] as const
export type DateBucket = (typeof DATE_BUCKETS)[number]

/** Weeks start on Monday. */
export function dateBucket(iso: string, now = Date.now()): DateBucket {
  const t = new Date(iso).getTime()
  const today = startOfDay(now)
  if (t >= today) return 'Today'
  if (t >= today - 86_400_000) return 'Yesterday'
  const dow = (new Date(today).getDay() + 6) % 7 // Monday = 0
  const weekStart = today - dow * 86_400_000
  if (t >= weekStart) return 'Earlier this week'
  if (t >= weekStart - 7 * 86_400_000) return 'Last week'
  return 'Older'
}

export type ScanSource = 'upload' | 'github' | 'url'

export function sourceOf(repoUrl: string): ScanSource {
  if (isUploadScan(repoUrl)) return 'upload'
  if (repoUrl.startsWith('https://github.com/')) return 'github'
  return 'url'
}

/** "Uploaded folder", "GitHub · main", "Server folder", "Link · main". */
export function sourceLabel(repoUrl: string, branch: string): string {
  if (isUploadScan(repoUrl)) return 'Uploaded folder'
  if (repoUrl.startsWith('local://')) return 'Server folder'
  if (repoUrl.startsWith('https://github.com/')) return `GitHub · ${branch}`
  return `Link · ${branch}`
}

export { repoLabel }

/** The findings column of a row. */
export function findingsLabel(key: ScanStatusKey, sev: SeverityCounts | undefined, liveCount?: number): string {
  const n = liveCount ?? sevTotal(sev)
  if (key === 'queued' || key === 'scheduled') return 'Not started'
  if (key === 'failed') return 'No findings, it did not run'
  if (key === 'stopped') return 'No report, it was stopped'
  if (key === 'running' || key === 'paused') return `${n} found so far`
  const line = sevLine(sev)
  return `${n} ${n === 1 ? 'finding' : 'findings'}${line ? ` · ${line}` : ''}`
}

/** The one explanation sentence under a row's name. Empty for a plain finish. */
export function scanSentence(key: ScanStatusKey, scan: ScanStatusResponse): string {
  switch (key) {
    case 'partial': {
      const failed = scan.agents_failed?.length ?? 0
      return failed
        ? `${failed} ${failed === 1 ? 'agent' : 'agents'} could not run, so the results leave those areas out.`
        : 'Some checks could not run, so the results leave those areas out.'
    }
    case 'failed':
      return scan.failure_message
        ? `${scan.failure_message}${scan.failure_remedy ? ` ${scan.failure_remedy}` : ''}`
        : 'The scan failed before it could report. Open it to see what happened.'
    case 'stopped':
      return 'You stopped it. Agents that had not started did not run.'
    case 'paused':
      return 'Paused · nothing runs until you resume.'
    case 'queued':
      return 'Starts when a running scan finishes.'
    default:
      return ''
  }
}

export function pendingSentence(key: ScanStatusKey, p: PendingScan): string {
  const pushes = p.trigger_count > 1 ? ` It stands for ${p.trigger_count} requests that arrived close together, scanned once.` : ''
  if (key === 'scheduled') return `Starts by itself at ${hm(p.run_after)}.${pushes}`
  return `Starts when a running scan finishes.${pushes}`
}

/** "Wave 3 of 4 · Architecture is running now · 9 of 13 agents done" (DEC-191). */
export function liveSentence(live: LiveScanSummary, status: ScanStatusKey): string {
  const done = `${live.agents_done} of ${live.agents_total} agents done`
  if (!live.has_events || live.wave === null) {
    return status === 'paused'
      ? 'Paused · nothing runs until you resume.'
      : 'Getting ready. Progress shows once the first agent reports.'
  }
  if (status === 'paused') {
    return `Paused during wave ${live.wave} · ${done} · nothing runs until you resume`
  }
  const who = live.current_agent ? `${live.current_agent} is running now` : 'The next agent starts shortly'
  return `Wave ${live.wave} of ${live.waves_total} · ${who} · ${done}`
}


/** Days between a paused scan's start and the moment its extra options appear. */
function waitDays(scan: Pick<ScanStatusResponse, 'started_at' | 'archive_available_at'>): number | null {
  if (!scan.archive_available_at) return null
  const ms = new Date(scan.archive_available_at).getTime() - new Date(scan.started_at).getTime()
  return Number.isFinite(ms) ? Math.round(ms / 86_400_000) : null
}

/** True once a paused scan may be archived or deleted permanently (DEC-192 decision 1). */
export function archiveOffered(
  scan: Pick<ScanStatusResponse, 'archive_available_at'>,
  now = Date.now(),
): boolean {
  if (!scan.archive_available_at) return false
  return new Date(scan.archive_available_at).getTime() <= now
}

/** The paused row's sentence about when Archive and Delete permanently appear. */
export function pausedOfferSentence(
  scan: Pick<ScanStatusResponse, 'started_at' | 'archive_available_at'>,
  now = Date.now(),
): string {
  const days = waitDays(scan)
  if (days === null || !scan.archive_available_at) return ''
  if (archiveOffered(scan, now)) {
    return `Paused for more than ${days} ${days === 1 ? 'day' : 'days'}. You can archive it or delete it permanently.`
  }
  return `Archive and Delete permanently are offered after ${days} ${days === 1 ? 'day' : 'days'} paused, on ${dateLabel(scan.archive_available_at)}.`
}

/** A deleted record's sentence (DEC-192 decision 3). */
export function deletedSentence(scan: Pick<ScanStatusResponse, 'deleted_at' | 'deleted_by_name'>): string {
  if (!scan.deleted_at) return ''
  return `Deleted on ${dateLabel(scan.deleted_at)} by ${scan.deleted_by_name ?? 'a workspace member'}.`
}

/** How many findings a permanent delete erases, or erased. */
export function erasedCount(scan: ScanStatusResponse): number {
  if (scan.deleted_at) return scan.deleted_findings_count ?? 0
  return sevTotal(scan.findings_by_severity) || scan.finding_count || 0
}

/** The exact confirmation copy of Delete permanently (DEC-192 decision 3). */
export function purgeConfirmText(n: number): string {
  return `This erases ${n} ${n === 1 ? 'finding' : 'findings'} and cannot be undone. A record that the scan existed stays in Archived.`
}
